/**
 * Control-circuit intelligence: a relay is identified by its tag (K1): its "head" (coil,
 * timer coil, thermal overload element) and its contacts, wherever they are drawn.
 * From that we derive:
 * - cross-references: each contact shows where its coil is ("/2.3" = sheet 2, column 3);
 *   each coil lists its contacts with their terminal numbers and locations (contact mirror);
 * - terminal numbers per EN 50005 / IEC 61666: coils A1–A2, main contacts 1–2, 3–4, 5–6,
 *   auxiliary contacts n3–n4 (NO), n1–n2 (NC), n7–n8 / n5–n6 (delayed), changeover n1-n2-n4,
 *   overload contacts 95–96;
 * - terminal-strip labels "X1:3".
 * Nothing is stored: everything follows the drawing.
 */
import { sheetsInOrder, type Element, type Id, type Project } from '@elec/domain';
import { columnAt } from '@elec/layout';
import { getSymbol, portDirection, portPosition } from '@elec/symbols';
import { offpageKey } from './topology.ts';

export const HEAD_SYMBOLS = new Set([
  'iec.coil',
  'iec.coil-on-delay',
  'iec.coil-off-delay',
  'iec.coil-impulse',
  'iec.overload',
  'iec.overload-3p',
]);

type ContactKind = 'no' | 'nc' | 'co' | 'no-delay' | 'nc-delay' | 'main' | 'main3' | 'ol';
export const CONTACT_SYMBOLS: Record<string, ContactKind> = {
  'iec.contact-no': 'no',
  'iec.contact-nc': 'nc',
  'iec.contact-changeover': 'co',
  'iec.contact-no-delay': 'no-delay',
  'iec.contact-nc-delay': 'nc-delay',
  'iec.contactor': 'main',
  'iec.contactor-3p': 'main3',
  'iec.overload-contact': 'ol',
};
const KIND_LABEL: Record<ContactKind, string> = {
  no: 'NO',
  nc: 'NF',
  co: 'INV',
  'no-delay': 'NO-T',
  'nc-delay': 'NF-T',
  main: '',
  main3: '',
  ol: 'NF',
};

export interface Relay {
  tag: string;
  heads: Element[];
  contacts: Element[];
}

export interface PortLabel {
  x: number;
  y: number;
  text: string;
  anchor: 'start' | 'end' | 'middle';
}

export interface Annotation {
  /** Extra label lines (cross-references). */
  extra: string[];
  /** Terminal numbers drawn next to ports. */
  ports: PortLabel[];
}

export interface ControlInfo {
  relays: Map<string, Relay>;
  /** Terminal designation of a port, e.g. "13" or "A1" (undefined if not numbered). */
  terminal(el: Element, portId: string): string | undefined;
  /** "sheet.column" of an element, e.g. "2.3". */
  location(el: Element): string;
  annotations: Map<Id, Annotation>;
}

const cache = new WeakMap<Project, ControlInfo>();

export function controlInfo(project: Project): ControlInfo {
  const hit = cache.get(project);
  if (hit) return hit;
  const sheets = sheetsInOrder(project);
  const sheetNo = new Map(sheets.map((s, i) => [s.id, i + 1]));
  const order = (e: Element) => [sheetNo.get(e.sheetId) ?? 0, e.x, e.y] as const;
  const cmp = (a: Element, b: Element) => {
    const [sa, xa, ya] = order(a);
    const [sb, xb, yb] = order(b);
    return sa - sb || xa - xb || ya - yb;
  };
  const location = (el: Element) => {
    const sheet = project.sheets[el.sheetId];
    const def = getSymbol(el.symbolId);
    const cx = el.x + (def ? def.width / 2 : 0);
    return sheet ? `${sheetNo.get(sheet.id)}.${columnAt(sheet, cx)}` : '?';
  };

  const relays = new Map<string, Relay>();
  for (const el of Object.values(project.elements)) {
    if (!el.tag) continue;
    const head = HEAD_SYMBOLS.has(el.symbolId);
    const contact = el.symbolId in CONTACT_SYMBOLS;
    if (!head && !contact) continue;
    const r = relays.get(el.tag) ?? { tag: el.tag, heads: [], contacts: [] };
    (head ? r.heads : r.contacts).push(el);
    relays.set(el.tag, r);
  }

  // Terminal numbers per port.
  const terms = new Map<Id, Record<string, string>>();
  for (const r of relays.values()) {
    r.heads.sort(cmp);
    r.contacts.sort(cmp);
    for (const h of r.heads) {
      if (h.symbolId === 'iec.overload') terms.set(h.id, { in: '1', out: '2' });
      else if (h.symbolId === 'iec.overload-3p')
        terms.set(h.id, { in1: '1', out1: '2', in2: '3', out2: '4', in3: '5', out3: '6' });
      else terms.set(h.id, { in: 'A1', out: 'A2' });
    }
    let aux = 0;
    let main = 0;
    for (const c of r.contacts) {
      const k = CONTACT_SYMBOLS[c.symbolId]!;
      if (k === 'main') {
        main++;
        terms.set(c.id, { in: String(2 * main - 1), out: String(2 * main) });
      } else if (k === 'main3') {
        terms.set(c.id, { in1: '1', out1: '2', in2: '3', out2: '4', in3: '5', out3: '6' });
      } else if (k === 'ol') {
        terms.set(c.id, { in: '95', out: '96' });
      } else {
        aux++;
        const n = String(aux % 10);
        const map: Record<Exclude<ContactKind, 'main' | 'main3' | 'ol'>, Record<string, string>> = {
          no: { in: `${n}3`, out: `${n}4` },
          nc: { in: `${n}1`, out: `${n}2` },
          'no-delay': { in: `${n}7`, out: `${n}8` },
          'nc-delay': { in: `${n}5`, out: `${n}6` },
          co: { c: `${n}1`, nc: `${n}2`, no: `${n}4` },
        };
        terms.set(c.id, map[k]);
      }
    }
  }
  const terminal = (el: Element, portId: string) => terms.get(el.id)?.[portId];

  const annotations = new Map<Id, Annotation>();
  const portLabels = (el: Element): PortLabel[] => {
    const def = getSymbol(el.symbolId);
    const t = terms.get(el.id);
    if (!def || !t) return [];
    const out: PortLabel[] = [];
    for (const port of def.ports) {
      const text = t[port.id];
      if (!text) continue;
      const p = portPosition(def, el, port);
      const dir = portDirection(port, el);
      if (dir === 'up') out.push({ x: p.x - 0.8, y: p.y + 2.2, text, anchor: 'end' });
      else if (dir === 'down') out.push({ x: p.x - 0.8, y: p.y - 0.8, text, anchor: 'end' });
      else out.push({ x: p.x + (dir === 'left' ? 2 : -2), y: p.y - 0.8, text, anchor: 'middle' });
    }
    return out;
  };
  for (const r of relays.values()) {
    const headLoc = r.heads[0] ? location(r.heads[0]) : undefined;
    for (const h of r.heads) {
      const mirror = r.contacts
        .filter((c) => CONTACT_SYMBOLS[c.symbolId] !== 'ol' || h.symbolId.startsWith('iec.overload'))
        .slice(0, 8)
        .map((c) => {
          const k = CONTACT_SYMBOLS[c.symbolId]!;
          const tt = terms.get(c.id) ?? {};
          const pair =
            k === 'co' ? `${tt.c}-${tt.nc}-${tt.no}` : k === 'main3' ? '1-2 3-4 5-6' : `${tt.in}-${tt.out}`;
          return `${pair}${KIND_LABEL[k] ? ` ${KIND_LABEL[k]}` : ''} /${location(c)}`;
        });
      annotations.set(h.id, { extra: mirror, ports: portLabels(h) });
    }
    for (const c of r.contacts)
      annotations.set(c.id, { extra: headLoc ? [`/${headLoc}`] : [], ports: portLabels(c) });
  }

  // Off-page connectors: show where the conductor continues ("/3.1").
  const byLink = new Map<string, Element[]>();
  for (const el of Object.values(project.elements)) {
    if (el.symbolId !== 'iec.offpage') continue;
    const link = offpageKey(el);
    if (link) byLink.set(link, [...(byLink.get(link) ?? []), el]);
  }
  for (const list of byLink.values())
    for (const el of list)
      annotations.set(el.id, {
        extra: list.filter((o) => o.id !== el.id).map((o) => `/${location(o)}`),
        ports: [],
      });

  const info: ControlInfo = { relays, terminal, location, annotations };
  cache.set(project, info);
  return info;
}

/** Terminal label ("X1:3") of a terminal element. */
export function terminalLabel(el: Element): string {
  const n = el.props.number;
  return n !== undefined && n !== '' ? `${el.tag}:${n}` : el.tag;
}

/** Human description of what a port connects to: "K1:A1", "X1:3", "Q3 out". */
export function portRef(project: Project, el: Element, portId: string): string {
  if (el.symbolId === 'iec.terminal') return terminalLabel(el);
  const t = controlInfo(project).terminal(el, portId);
  return t
    ? `${el.tag}:${t}`
    : `${el.tag || getSymbol(el.symbolId)?.name.fr}${portId === 'in' || portId === 'out' ? '' : `:${portId}`}`;
}
